<template>
    <w-loader v-if="loading"/>
    <div v-else>
        <w-alert
            v-if="alert"
            :message="alert.message"
            :level="alert.level"
            class="mb-6"
        />
        <w-form
            v-if="account"
            @submit="submitted"
            @changed="may_submit = true"
            class="account-settings"
            data-discard="true"
        >
            <div class="w-h6 info--text text--darken-1" data-test-id="account-settings-general-title">
                {{ l.t("app.general", "General") }}
            </div>
            <FormInput :label="l.t('app.timezone', 'Timezone')">
                <TimezoneSelector
                    v-model="account.timezone"
                    data-test-id="account-settings-timezone-selector"
                />
            </FormInput>
            <FormInput :label="l.t('accounts.prefix-dialing-out', 'Prefix for dialing out')">
                <w-select
                    v-model="account.dial_prefix"
                    :items="[{value: '', text: l.t('app.none-lowercase', 'none')}, '2', '3', '4', '5', '6', '7', '8', '9']"
                    hide-details="auto"
                    data-test-id="account-settings-prefix-select"
                />
            </FormInput>
            <FormInput :label="l.t('accounts.directory-type', 'Dial-by-name directory type')">
                <w-select
                    v-model="account.directory_type"
                    :items="[
                        {text: l.t('app.last-name', 'Last name'), value: 'last_name'},
                        {text: l.t('app.first-name', 'First name'), value: 'first_name'}
                    ]"
                    hide-details="auto"
                    data-test-id="account-settings-dial-by-name-select"
                />
            </FormInput>
            <div class="mt-5 info--text text--darken-1 w-subtitle-1 mt-7" data-test-id="account-settings-default-ext-title">
                {{ l.t("accounts.default-extension", "Default extension") }}
            </div>
            <FormInput :label="l.t('app.extension', 'Extension')">
                <APIAutocomplete
                    v-model="account.default_voip_phone_id"
                    mode="extensions"
                    data-test-id="account-settings-default-ext-selector"
                />
            </FormInput>
            <ClassicOrNxt user="classic" tag="div">
                <div class="info--text text--darken-1 w-subtitle-1 mt-8">
                    {{ l.t('app.restricted-outbound-caller-id', 'Restricted Outbound Caller ID')}}
                </div>
                <div class="w-caption mt-4">
                    {{ l.t('accounts.if-enabled-all-extensions', 'If enabled, all extensions on the account will only have the option to display caller ID numbers configured to send calls to their extension.') }}
                </div>
                <div class="recording-option">
                    <div class="shorter-settings-label w-body-2">
                    {{ l.t('accounts.restricted', 'Restricted')}}
                    </div>
                    <w-switch v-model="account.is_restricted" :label="account.is_restricted ? l.t('app.on', 'On') : l.t('app.off', 'Off')"/>
                </div>
            </ClassicOrNxt>

            <div class="info--text text--darken-1 w-subtitle-1 moh-advanced-label">
                {{l.t('app.hold-music', 'Hold music')}}
            </div>
            <FormInput>
                <div class="d-flex">
                    <div class="premium-moh-selector mt-n8">
                        <w-radio-group
                            v-model="account.features['premium-music']"
                            @change="check_if_premium_is_disabled"
                            row
                            hide-details="auto"
                            data-test-id="premium-moh-selector"
                        >
                            <v-radio
                                v-for="t of types"
                                color="text lighten-1"
                                :label="t.text"
                                type="radio"
                                :value="t.value"
                                :data-test-id="`premium-moh-selector-${t.value}`"
                            />
                        </w-radio-group>
                        <div class="text--text text--lighten-1 w-helper-text type-radio-selector-helper-text">
                            {{ l.t('app.premium-music-includes', 'Premium music includes hundreds of licensed songs by popular artists. Configure different genres for different call handling rules and change them at any time.') }}
                        </div>
                    </div>
                </div>
            </FormInput>
            <FormInput :label="l.t('account.default-hold-music', 'Default hold music')">
                <div class="d-flex">
                    <MOHSelector
                        v-model="account.hold_music"
                        :nullable="true"
                        :show_premium="true"
                        :force_enable_premium="!account.features['premium-music']"
                        :force_disable_premium="!account.features['premium-music']"
                        :key="`moh-selector-${account.features['premium-music']}`"
                    />
                </div>
            </FormInput>
            <ClassicOrNxt user="classic" tag="div">
                <div class="mt-7 mb-1 info--text text--darken-1 w-subtitle-1" id="call-recording" data-test-id="account-settings-call-recording-title">
                    {{ l.t("accounts.call-recording", "Call recording") }}
                </div>
                <HelpText class="pt-1 pb-1" data-test-id="account-settings-cr-help-text">
                    {{ l.t('account.different-laws-warning', 'There are different laws that apply to call recording. Please check your current state and other party’s state and Federal laws before using this feature.') }}
                </HelpText>
                <div class="recording-option">
                    <div class="shorter-settings-label w-body-2">
                        {{ l.t('accounts.recording-status', 'Recording status')}}
                    </div>
                    <div>
                        <!-- <w-radio-group v-model="account.features['call-recording-on']" disabled row hide-details="auto" data-test-id="callrecording-status">
                            <v-radio
                                :value="false"
                                color="text lighten-1"
                                :label="l.t('app.off', 'Off')"
                                data-test-id="disable-call-recording"
                            />
                            <v-radio
                                :value="true"
                                color="text lighten-1"
                                :label="l.t('app.on','On')"
                                data-test-id="enable-call-recording"
                            />
                        </w-radio-group> -->
                        <div class="d-flex flex-row gap-10 align-center">
                            <w-select
                                v-model="recording_plan_value"
                                :items="recording_options"
                                :key="recording_plan_value"
                                @change="on_plan_change"
                            />
                            <w-btn
                                v-if="recording_plan_value?.includes('transcribe')"
                                @click="show_transcription_modal = true"
                                data-test-id="account-settings-transcription-info-btn"
                                icon
                            >
                                <v-icon size="24" color="darken-1" class="cursor-pointer">$vuetify.icons.infotip_stylized</v-icon>
                            </w-btn>
                        </div>
                        <div class="w-helper-text text--text text--lighten-1 recording-helper-text">
                            <span v-if="recording_plan_value">{{ l.t('accounts.if-recording-exceed', 'If recordings exceed the allotted number of minutes, there will be an additional charge of 3 cents per minute.')}}</span>
                        </div>
                    </div>
                </div>

                <div class="recording-option">
                    <div class="shorter-settings-label w-body-2">
                        {{ l.t('accounts.recording-mode', 'Recording mode')}}
                    </div>
                    <div class="w-helper-text text--text text--lighten-1">
                        <w-select
                            v-model="account.record_all_calls"
                            :placeholder="!account.features['call-recording-300'] && !account.features['call-recording-1000'] ? l.t('app.none', 'None') : l.t('accounts.please-choose-calls-to-record', 'Please choose calls to record')"
                            :items="[
                                { value: 'incoming', text: l.t('app.incoming', 'Incoming') },
                                { value: 'outgoing', text: l.t('app.outgoing', 'Outgoing') },
                                { value: 'all', text: l.t('app.all', 'All') },
                                { value: 'custom', text: l.t('app.custom', 'Custom') },
                            ]"
                            :disabled="recording_plan_value === null"
                        />
                        <div class="w-helper-text text--text text--lighten-1 recording-helper-text">
                            {{ l.t('account.record-incomin-and-outgoing', 'Record incoming and outgoing calls based on my per number settings')}}
                        </div>
                    </div>
                </div>
                <div class="recording-option">
                    <div class="shorter-settings-label w-body-2">
                        {{ l.t('accounts.recording-beep', 'Recording beep')}}
                    </div>
                    <div>
                        <w-switch v-model="account.call_recording_beep"
                            :label="account.call_recording_beep ? l.t('app.on', 'On') : l.t('app.off', 'Off')" hide-details='auto'
                            data-test-id="account-settings-beep-switch"
                        />
                        <div class="w-helper-text text--text text--lighten-1 mt-0  mb-0 recording-helper-text">
                            {{ l.t('accounts.a-beep=will-sound-every-15', 'A beep will sound every 15 seconds. Learn more on the legality surrounding Call recording and reasons for the beep.')}}
                        </div>
                    </div>
                </div>
            </ClassicOrNxt>
            <div class="d-flex justify-center mt-11">
                <CancelChanges v-if="inside_modal" @click:cancel="$emit('click:cancel')"/>
                <w-btn
                    :disabled="!may_submit"
                    color="primary"
                    type="submit"
                    class="ml-5"
                    data-test-id="account-settings-submit-btn"
                >
                    {{ l.t('app.save', 'Save') }}
                </w-btn>
            </div>
        </w-form>
        <DefaultModal
            v-model="show_transcription_modal"
            :with_x="false"
            @close="show_transcription_modal = false"
            width="800"
        >
            <div class="w-h5 text-center mb-7 font-weight-600">
                {{ l.t('transcription.steps-to-enable-call-transcription', 'Steps to Enable Call Transcription') }}
            </div>
            <div class="w-body-2">
                <ol class="gap-20 d-flex flex-column">
                    <li v-if="account.record_all_calls === 'custom'">
                        <div class="font-weight-600">
                            Enable Recording on Your Phone Number
                            <w-btn
                                @click="show_transcription_edit = true; modal_type = 'phone-numbers'; show_transcription_modal = false"
                                class="action lighten-1 ml-1"
                                width="20"
                                height="20"
                                icon
                            >
                                <v-icon size="18">$vuetify.icons.edit</v-icon>
                            </w-btn>
                        </div>
                        <div>If Custom recording mode is specified, ensure that your phone number is configured to record calls. This setting is typically found in your Number Settings.</div>
                    </li>
                    <li>
                        <div class="font-weight-600">
                            Link Transcriptions to Your User/Extension
                            <w-btn
                                @click="show_transcription_edit = true; modal_type = 'video'; show_transcription_modal = false"
                                class="action lighten-1 ml-1"
                                width="20"
                                height="20"
                                icon
                            >
                                <v-icon size="18">$vuetify.icons.edit</v-icon>
                            </w-btn>
                        </div>
                        <div>Transcriptions are associated with a specific user/extension and will appear alongside your video transcriptions. Verify that your user/extension has an associated Video Bridge and that the e-mail related to the Video Bridge is correct.</div>
                    </li>
                    <li>
                        <div class="font-weight-600">
                            Forward Calls for Transcription
                            <w-btn
                                @click="show_transcription_edit = true; modal_type = 'phone-numbers'; show_transcription_modal = false"
                                class="action lighten-1 ml-1"
                                width="20"
                                height="20"
                                icon
                            >
                                <v-icon size="18">$vuetify.icons.edit</v-icon>
                            </w-btn>
                        </div>
                        <div>Only calls forwarded from the recorded number to your user/extension will be transcribed. Set up call forwarding rules accordingly to route incoming calls to the appropriate extension.</div>
                    </li>
                    <li>
                        <div class="font-weight-600">Where do users see the transcriptions?</div>
                        <div>All Users/extensions that are correctly set to accept recorded calls will generate a transcription that will be sent to the user via the email set on that user/Extension video Bridge. As the Admin, you can find/search transcriptions of all users under the Video Conferencing section.</div>
                    </li>
                    <li>
                        <div class="font-weight-600">Additional fees may apply.</div>
                        <div>Please check your billing statement after activation.</div>
                    </li>
                </ol>
            </div>
            <template v-slot:buttons>
                <w-btn color="primary" @click.prevent="show_transcription_modal = false">{{ $l.t('app.ok', 'Ok') }}</w-btn>
            </template>
        </DefaultModal>
        <DefaultModal
            v-model="show_transcription_edit"
            @close="show_transcription_edit = false; modal_type = null; show_transcription_modal = true"
            :width="modal_type === 'phone-numbers' ? 550 : 800"
        >
            <template v-if="modal_type === 'phone-numbers'" >
                <div class="w-body-1 mb-7 font-weight-600">Please select the phone number you would like to edit</div>
                <APIAutocomplete
                    mode="phone_numbers"
                    data-test-id="account-settings-transcription-edit-phone-number"
                />
            </template>
            <!-- <PhoneNumbers v-if="modal_type === 'phone-numbers'" :tabs="['phone-numbers.index']"/> -->
            <VideoConference v-else :tabs="['video.index', 'video.users.index', 'video.users.create']"/>
        </DefaultModal>
        <DefaultModal
            v-model="show_premium_modal"
            :with_x="true"
            @close="show_premium_modal = false"
            width="500"
            :key="`selector-create-modal-${show_premium_modal}`"
        >
            <div class="error--text w-h5 mb-4">{{l.t('app.alert', 'Alert')}}!</div>
            {{l.t('account.premium-change-alert', 'If saving this setting as "disabled", please check any Queue or Call Handling Rule that is currently using any Premium Hold Music, as that setting will be set to a default Standard Hold Music')}}
        </DefaultModal>
    </div>
</template>

<script>
import { consoleUserCanAccessPageMiddleware } from '../../libs/helpers2';
import l from '../../libs/lang';
import HelpText from '../elements/HelpText.vue';
import MOHSelector from '../elements/MOHSelector.vue';
import FormInput from '../elements/form/FormInput.vue';
import SectionTitle from '../elements/SectionTitle.vue';
import ClassicOrNxt from '../elements/ClassicOrNxt.vue';
import CancelChanges from '../elements/CancelChanges.vue';
import PhoneNumbers from '../screens/phone-numbers/master.vue';
import VideoConference from '../screens/video/master.vue';
import DefaultModal from '../elements/modal/DefaultModal.vue';
import TimezoneSelector from '../elements/TimezoneSelector.vue';
import APIAutocomplete from '../elements/form/APIAutocomplete.vue';

export default {
    props: ['_account', 'scroll_to'],
    components: {
        HelpText,
        FormInput,
        MOHSelector,
        SectionTitle,
        ClassicOrNxt,
        DefaultModal,
        PhoneNumbers,
        CancelChanges,
        APIAutocomplete,
        VideoConference,
        TimezoneSelector,
    },
    data() {
        return {
            l,
            loading: false,
            alert: null,
            account: null,
            inside_modal: this?.$parent?.$el?.role === 'dialog' || this?.$parent?.$el?.classList?.contains('v-dialog'),
            types: [
                { value: false, text: l.t('app.basic', 'Basic') },
                { value: true, text: l.t('app.premium-299', 'Premium  $2.99/mo') },
            ],
            recording_options: [
                { value: null, text: l.t('app.off', 'Off') },
                { value: 'call-recording-300', text: l.t('account.300min-4-95', '300 minutes - $4.95 per month') },
                { value: 'call-recording-1000', text: l.t('account.1000min-14-95', '1000 minutes - $14.95 per month') },
            ],
            may_submit: false,
            modal_type: null,
            show_transcription_edit: false,
            show_transcription_modal: false,
            original_premium_moh_enabled: false,
            show_premium_modal: false,
            recording_plan_value: null,
        };
    },
    async created() {
        this.$data.loading = true;
        const route = this.$router.options.routes.find((x) => x.name === 'video.index');
        const user_can_access_meetings = await consoleUserCanAccessPageMiddleware(route, this.$branding, this.$session);
        if (user_can_access_meetings) {
            this.$data.recording_options.push(
                { value: 'call-recording-300-transcribe', text: l.t('account.300-minutes-w-transcription', '300 minutes w/ transcription - $24.95 per month') },
                { value: 'call-recording-1000-transcribe', text: l.t('account.1000-minutes-w-transcription', '1000 minutes w/ transcription - $34.95 per month') }
            );
        }
        this.$data.account = this.$props._account;
        this.$data.original_premium_moh_enabled = !!this.$data.account.features['premium-music'];
        this.$data.recording_plan_value = this.calculate_recording_plan_value();
        this.scroll_if_needed();
        this.$data.loading = false;
    },
    methods: {
        scroll_if_needed() {
            if (this.$props.scroll_to) {
                setTimeout(() => {
                    const el = document.getElementById(this.$props.scroll_to);
                    if (el) {
                    el.scrollIntoView({behavior: 'smooth', block: 'start'});
                    }
                }, 10);
            }
        },
        on_plan_change(data) {
            for (const option of this.$data.recording_options) {
                if (option.value) {
                    this.$data.account.features[option.value] = option.value === data;
                }
            }
            if (data.includes('transcribe')) {
                this.$data.show_transcription_modal = true;
            }
        },
        submitted(e) {
            if (e) e.preventDefault();
            this.$emit('submitted', this.$data.account);
            this.$data.may_submit = false;
        },
        check_if_premium_is_disabled() {
            if (!this.$data.account.features['premium-music'] && this.$data.original_premium_moh_enabled) {
                this.show_premium_modal = true;
            }
        },
        calculate_recording_plan_value() {
            for (const option of this.$data.recording_options) {
                if (option.value && this.$data.account.features[option.value]) {
                    return option.value;
                }
            }
            return null;
        }
    },
};
</script>
<style lang="scss">
.account-settings {
    .shorter-settings-label {
        width: 150px;
        margin-top: 16px;
    }
    .moh-advanced-label {
        @extend .shorter-settings-label;
        margin-top: 11px;
    }
    .recording-option {
        display: flex;
        margin-top: 9px;
        .custom-select {
            max-width: 300px !important;
        }
        .recording-helper-text {
            max-width: 375px;
            margin-top: 12px;
            margin-bottom: 12px;
        }
    }
    .premium-moh-selector {
        .type-radio-selector-helper-text {
            margin-top: -3px;
            margin-bottom: 11px;
            max-width: 325px;
        }
    }
    .selector-wrapper .v-input {
        width: 300px;
    }
}
</style>
